import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Reviews.module.css";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import FiltersBar from "../analytics/components/FiltersBar";
import KpiCard from "../analytics/components/KpiCard";
import ExportButton from "../analytics/components/ExportButton";
import ReviewsList from "./components/ReviewsList";
import RatingStars from "./components/RatingStars";
import { useReviews } from "./hooks/useReviews";
import { useTracksForReviews } from "./hooks/useTracksForReviews";
import { useExportReviewsCsv } from "./hooks/useExportReviewsCsv";
import { useOwnerAccess } from "../../hooks/useOwnerAccess";
import { SelectOption } from "../analytics/types";
import { startOfDay, endOfDay } from "../analytics/utils/deriveDateBuckets";
import { formatDecimal, formatInteger } from "../analytics/utils/formatNumber";
import { buildReviewsTrackIdsPayload } from "./buildReviewsTrackIdsPayload";

const REVIEWS_MAX_DATE_RANGE_DAYS = 366;
const DEFAULT_RANGE_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const daysAgo = (days: number): Date => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d;
};

/** Same rule as `getReviews` / analytics: inclusive local day bounds, max span `maxDays`. */
const isDateRangeWithinMaxDays = (start: Date, end: Date, maxDays: number): boolean => {
    const a = startOfDay(start).getTime();
    const b = endOfDay(end).getTime();
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return false;
    return b - a <= maxDays * MS_PER_DAY;
};

const Reviews: React.FC = () => {
    const { t } = useTranslation();
    const tt = t as unknown as (k: string, opts?: Record<string, unknown>) => string;
    const { hasAccess: isOwner, loading: ownerLoading } = useOwnerAccess();

    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const [selectedTrackIds, setSelectedTrackIds] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(daysAgo(DEFAULT_RANGE_DAYS));
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    /** Snapshot of the filters used by the last successful fetch — drives the
     *  KPI helper labels and the CSV export, so the export can never desync
     *  from the data the user is actually looking at. */
    const [appliedQuery, setAppliedQuery] = useState<{
        ids: string[];
        start: Date;
        end: Date;
    } | null>(null);

    const { tracks, loading: tracksLoading } = useTracksForReviews();
    const { data, loading, fetch, reset } = useReviews();
    const { exporting, run: runExport } = useExportReviewsCsv();

    const companyOptions = useMemo<SelectOption[]>(() => {
        const byId = new Map<string, string>();
        for (const tr of tracks) {
            if (tr.companyId) {
                byId.set(tr.companyId, tr.companyName || tr.companyId);
            }
        }
        return Array.from(byId.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    }, [tracks]);

    const showCompanyFilter = isOwner && companyOptions.length > 1;

    const tracksForPicker = useMemo(() => {
        if (!isOwner || !showCompanyFilter) {
            return tracks;
        }
        if (selectedCompanyIds.length === 0) {
            return tracks;
        }
        return tracks.filter((tr) => tr.companyId && selectedCompanyIds.includes(tr.companyId));
    }, [tracks, isOwner, showCompanyFilter, selectedCompanyIds]);

    const trackIdsPayloadForApi = useMemo(
        () =>
            buildReviewsTrackIdsPayload({
                isOwner,
                showCompanyFilter,
                selectedCompanyIds,
                selectedTrackIds,
                tracksForPicker,
            }),
        [isOwner, showCompanyFilter, selectedCompanyIds, selectedTrackIds, tracksForPicker],
    );

    const didAutoFetchRef = useRef(false);
    useEffect(() => {
        if (ownerLoading || tracksLoading) return;
        if (didAutoFetchRef.current) return;
        if (!startDate || !endDate) return;
        if (!isDateRangeWithinMaxDays(startDate, endDate, REVIEWS_MAX_DATE_RANGE_DAYS)) return;

        const payload = buildReviewsTrackIdsPayload({
            isOwner,
            showCompanyFilter,
            selectedCompanyIds,
            selectedTrackIds,
            tracksForPicker,
        });
        if (payload === null) return;

        didAutoFetchRef.current = true;
        const start = startOfDay(startDate);
        const end = endOfDay(endDate);
        fetch({ trackIds: payload, startDate: start, endDate: end }).then(() => {
            setAppliedQuery({ ids: payload, start, end });
        });
    }, [
        ownerLoading,
        tracksLoading,
        isOwner,
        showCompanyFilter,
        selectedCompanyIds,
        selectedTrackIds,
        tracksForPicker,
        startDate,
        endDate,
        fetch,
    ]);

    const options = useMemo<SelectOption[]>(
        () =>
            tracksForPicker.map((tr) => {
                const ratingLabel = tr.ratingsEnabled ? tt("reviewsRatingsOnLabel") : tt("reviewsRatingsOffLabel");
                const parts = [tr.companyName, ratingLabel].filter(Boolean);
                return {
                    value: tr.id,
                    label: tr.name,
                    description: parts.join(" · "),
                };
            }),
        [tracksForPicker, tt],
    );

    useEffect(() => {
        if (tracksForPicker.length === 0) {
            setSelectedTrackIds([]);
            return;
        }
        const allowed = new Set(tracksForPicker.map((tr) => tr.id));
        setSelectedTrackIds((prev) => prev.filter((id) => allowed.has(id)));
    }, [tracksForPicker]);

    const dateInputsWithinLimit = useMemo(() => {
        if (!startDate || !endDate) return false;
        return isDateRangeWithinMaxDays(startDate, endDate, REVIEWS_MAX_DATE_RANGE_DAYS);
    }, [startDate, endDate]);

    const canApply = dateInputsWithinLimit && trackIdsPayloadForApi !== null;

    const handleApply = async () => {
        if (!canApply || !startDate || !endDate || trackIdsPayloadForApi === null) return;
        const start = startOfDay(startDate);
        const end = endOfDay(endDate);
        const ids = trackIdsPayloadForApi.slice();
        await fetch({ trackIds: ids, startDate: start, endDate: end });
        setAppliedQuery({ ids, start, end });
    };

    const handleReset = () => {
        setSelectedCompanyIds([]);
        setSelectedTrackIds([]);
        const start = daysAgo(DEFAULT_RANGE_DAYS);
        const end = new Date();
        setStartDate(start);
        setEndDate(end);
        setAppliedQuery(null);
        reset();
    };

    const handleExport = () => {
        if (!appliedQuery || !dateInputsWithinLimit) return;
        runExport({
            trackIds: appliedQuery.ids,
            startDate: appliedQuery.start,
            endDate: appliedQuery.end,
        });
    };

    const averageScore = data?.averageScore ?? null;
    const totalReviews = data?.totalReviews ?? 0;
    const externalLinkRedirects = data?.externalLinkRedirects ?? 0;

    return (
        <div className={styles.page}>
            <TitleSubtitle title={tt("reviewsTitle")} subtitle={tt("reviewsSubtitle")} />

            <div className={styles.section}>
                <FiltersBar
                    selectorPlaceholder={tt("reviewsAllTracksPlaceholder")}
                    options={options}
                    selectedIds={selectedTrackIds}
                    onSelectedIdsChange={setSelectedTrackIds}
                    optionsLoading={tracksLoading}
                    companyFilter={
                        showCompanyFilter
                            ? {
                                  placeholder: tt("analyticsFilterCompanyPlaceholder"),
                                  options: companyOptions,
                                  selectedIds: selectedCompanyIds,
                                  onSelectedIdsChange: setSelectedCompanyIds,
                                  loading: tracksLoading,
                              }
                            : null
                    }
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                    maxRangeDays={REVIEWS_MAX_DATE_RANGE_DAYS}
                    canApply={canApply}
                    applying={loading}
                    onApply={handleApply}
                    onReset={handleReset}
                />

                <div className={styles.headerRow}>
                    <ExportButton
                        label={tt("exportCsv")}
                        loadingLabel={tt("exporting")}
                        onClick={handleExport}
                        disabled={exporting || !data || !appliedQuery || !dateInputsWithinLimit}
                        loading={exporting}
                    />
                </div>

                <div className={styles.kpiGrid}>
                    <KpiCard
                        title={tt("reviewsAverageScore")}
                        value={averageScore !== null ? `${formatDecimal(averageScore, 1)} / 5` : "—"}
                        help={tt("reviewsAverageScoreHelp")}
                        iconStack={averageScore !== null}
                        icon={averageScore !== null ? <RatingStars value={averageScore} size="regular" /> : null}
                    />
                    <KpiCard
                        title={tt("reviewsTotalCount")}
                        value={formatInteger(totalReviews)}
                        help={tt("reviewsTotalCountHelp")}
                    />
                    {data?.hasRatingLinkConfigured ? (
                        <KpiCard
                            title={tt("reviewsExternalLinkRedirects")}
                            value={formatInteger(externalLinkRedirects)}
                            help={tt("reviewsExternalLinkRedirectsHelp")}
                        />
                    ) : null}
                </div>

                {data?.reviewsListTruncated ? (
                    <p className={styles.listCapNotice} role="status">
                        {tt("reviewsListTruncatedNotice", {
                            max: data.reviewsListMaxRows,
                            total: formatInteger(data.totalReviews),
                        })}
                    </p>
                ) : null}

                <ReviewsList
                    reviews={data?.reviews || []}
                    title={tt("reviewsListTitle")}
                    countLabel={data ? tt("reviewsListCount", { count: data.reviews.length }) : ""}
                    emptyLabel={tt("reviewsListEmpty")}
                    noCommentLabel={tt("reviewsNoComment")}
                />
            </div>
        </div>
    );
};

export default Reviews;
